import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Sparkles, Briefcase, Users, Building2, LogOut, LayoutDashboard, Search, Bell, Shield, User, Inbox, BarChart3, ChevronDown, Contact, Wallet } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useState } from 'react';
import { GlobalSearch } from './GlobalSearch';
import { useUserRole } from '@/hooks/useUserRole';
import { NotificationBell } from '@/components/notifications';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

interface NavItem {
  path: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

const RecruiterLayout = ({ children }: { children: React.ReactNode }) => {
  const { signOut, user } = useAuth();
  const location = useLocation();
  const [searchOpen, setSearchOpen] = useState(false);
  const { role, isAdmin } = useUserRole();
  
  // Accounts section state (formerly Clients)
  const [accountsOpen, setAccountsOpen] = useState(
    location.pathname.includes('/recruiter/clients') || 
    location.pathname.includes('/recruiter/account-contacts')
  );
  
  // CRM section state
  const [crmOpen, setCrmOpen] = useState(
    location.pathname.includes('/recruiter/companies') || 
    location.pathname.includes('/recruiter/contacts')
  );

  const navItems: NavItem[] = [
    { path: '/recruiter', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/recruiter/jobs', label: 'Jobs', icon: Briefcase },
  ];

  const accountsItems: NavItem[] = [
    { path: '/recruiter/clients', label: 'Companies', icon: Building2 },
    { path: '/recruiter/account-contacts', label: 'Contacts', icon: Contact },
  ];

  const crmItems: NavItem[] = [
    { path: '/recruiter/companies', label: 'Companies', icon: Building2 },
    { path: '/recruiter/contacts', label: 'Contacts', icon: Contact },
  ];

  const bottomNavItems: NavItem[] = [
    { path: '/recruiter/candidates', label: 'Candidates', icon: Users },
    { path: '/recruiter/skills', label: 'Skills', icon: Sparkles },
    { path: '/recruiter/inbox', label: 'Email Inbox', icon: Inbox },
    { path: '/recruiter/performance', label: 'Performance', icon: BarChart3 },
    { path: '/recruiter/notifications', label: 'Notifications', icon: Bell },
  ];

  const isAccountsActive = accountsItems.some(item => location.pathname.startsWith(item.path));
  const isCrmActive = crmItems.some(item => location.pathname.startsWith(item.path));

  const renderNavItem = (item: NavItem, size: 'default' | 'sm' = 'default') => {
    const Icon = item.icon;
    const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
    
    return (
      <Link key={item.path} to={item.path}>
        <Button
          variant={isActive ? 'secondary' : 'ghost'}
          size={size}
          className={cn(
            'w-full justify-start',
            isActive && 'bg-primary/10 text-primary hover:bg-primary/20'
          )}
        >
          <Icon className="mr-2 h-4 w-4" />
          {item.label}
        </Button>
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-subtle">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 border-r bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b">
            <Link to="/recruiter" className="flex items-center gap-2">
              <Sparkles className="h-6 w-6 text-primary" />
              <span className="text-xl font-bold bg-gradient-primary bg-clip-text text-transparent">
                RecruitAI
              </span>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 overflow-y-auto">
            <div className="mb-4">
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => setSearchOpen(true)}
              >
                <Search className="mr-2 h-4 w-4" />
                Search...
                <kbd className="ml-auto pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground opacity-100">
                  <span className="text-xs">⌘</span>K
                </kbd>
              </Button>
            </div>
            <ul className="space-y-1">
              {/* Top nav items */}
              {navItems.map(item => (
                <li key={item.path}>{renderNavItem(item)}</li>
              ))}
              
              {/* Accounts Collapsible Section */}
              <li>
                <Collapsible open={accountsOpen} onOpenChange={setAccountsOpen}>
                  <CollapsibleTrigger asChild>
                    <Button
                      variant={isAccountsActive ? 'secondary' : 'ghost'}
                      className={cn(
                        'w-full justify-start',
                        isAccountsActive && 'bg-primary/10 text-primary hover:bg-primary/20'
                      )}
                    >
                      <Wallet className="mr-2 h-4 w-4" />
                      Accounts
                      <ChevronDown className={cn(
                        'ml-auto h-4 w-4 transition-transform',
                        accountsOpen && 'rotate-180'
                      )} />
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pl-4 mt-1 space-y-1">
                    {accountsItems.map(item => renderNavItem(item, 'sm'))}
                  </CollapsibleContent>
                </Collapsible>
              </li>
              
              {/* CRM Collapsible Section */}
              <li>
                <Collapsible open={crmOpen} onOpenChange={setCrmOpen}>
                  <CollapsibleTrigger asChild>
                    <Button
                      variant={isCrmActive ? 'secondary' : 'ghost'}
                      className={cn(
                        'w-full justify-start',
                        isCrmActive && 'bg-primary/10 text-primary hover:bg-primary/20'
                      )}
                    >
                      <Building2 className="mr-2 h-4 w-4" />
                      CRM
                      <ChevronDown className={cn(
                        'ml-auto h-4 w-4 transition-transform',
                        crmOpen && 'rotate-180'
                      )} />
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pl-4 mt-1 space-y-1">
                    {crmItems.map(item => renderNavItem(item, 'sm'))}
                  </CollapsibleContent>
                </Collapsible>
              </li>
              
              {/* Bottom nav items */}
              {bottomNavItems.map(item => (
                <li key={item.path}>{renderNavItem(item)}</li>
              ))}
            </ul>
            
            {/* Admin Panel Link */}
            {isAdmin && (
              <div className="mt-6 pt-4 border-t">
                <Link to="/admin">
                  <Button
                    variant="outline"
                    className="w-full justify-start border-destructive/50 text-destructive hover:bg-destructive/10"
                  >
                    <Shield className="mr-2 h-4 w-4" />
                    Admin Panel
                  </Button>
                </Link>
              </div>
            )}
          </nav>

          {/* User Section */}
          <div className="p-4 border-t">
            <div className="flex items-center justify-between mb-3 px-2">
              <div>
                <p className="text-sm font-medium truncate">{user?.email}</p>
                <p className="text-xs text-muted-foreground capitalize">{role || 'User'}</p>
              </div>
              <NotificationBell />
            </div>
            <div className="space-y-2">
              <Link to="/admin/profile">
                <Button
                  variant="ghost"
                  className="w-full justify-start"
                >
                  <User className="mr-2 h-4 w-4" />
                  My Profile
                </Button>
              </Link>
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => signOut()}
              >
                <LogOut className="mr-2 h-4 w-4" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-64">
        <div className="container mx-auto px-8 py-8">
          {children}
        </div>
      </main>
      
      <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />
    </div>
  );
};

export default RecruiterLayout;
